import { LinkButton } from '@/components/elements/forms/buttons';
import { ROUTE_BUILDER } from '@/constants/pages';
import { SEARCH_CONTEXT } from '@/features/searchV2/constants/tracking';
import { classnames } from '@/helpers';
import { filterEmptyValues } from '@/helpers/objects';
import { getLocationPreposition } from '@/helpers/seo';
import { DEFAULT_MY_LOCATION_KEYWORD } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { AppDispatch } from '@/store';
import { Fragment, useMemo } from 'react';
import GridLayoutSection from '../components/GridLayoutSection';
import HorizontalScrollSection from '../components/HorizontalScrollSection';
import { CARD_HEIGHT, MPServiceCardSkeleton } from '../components/MPServiceCard/MPServiceCard';
import { renderDealCard, renderSistaminutenCard } from '../helpers';
import { buildUrlSearchQuery } from '../hooks/useDealsSearch.helpers';
import { clearSmtOnlySearchToggle } from '../hooks/useSmtOnlyToggle';
import { DealSearchItem, DealsSearchPreviewResponse, SistaminutenSearchItem } from '../types/dealSearch';

export const HORIZONTAL_CARD_CONTAINER_CLASSNAME =
  'last-of-type:pr-md first-of-type:pl-md  w-[calc(100vw-3rem)] flex-shrink-0 snap-start md:max-w-[348px] only:w-full';

const baseTranslationKey = 'features.deals.modules.DealsSectionPreview';

type SectionLink =
  | {
      pathname: string;
      search?: string;
      state?: Record<string, unknown>;
    }
  | undefined;

function buildSectionLink(section: DealsSearchPreviewResponse['sections'][number], placeId?: number): SectionLink {
  if (!section.searchParams) return undefined;

  const { keyword, location, sort } = section.searchParams;

  /**
   * temporary overwrite the 'smt_discount' sort to the new 'highest_discount' on frontned until fixed on backend
   */
  const _sort = sort === 'smt_discount' ? 'highest_discount' : sort;

  const isMyLocation = location?.type === 'myLocation';
  const locationName = location?.name;

  const indexableKeyword = getKeywordWithSlug(keyword ?? '');
  const indexableLocation = getLocationWithSlug(isMyLocation ? DEFAULT_MY_LOCATION_KEYWORD : location?.name ?? '');

  let isIndexable = false;

  if (section.type === 'brand_deals') {
    isIndexable = indexableLocation?.slug ? true : false;
  } else {
    isIndexable = !!(indexableKeyword?.slug && indexableLocation?.slug);
  }

  let pathname = undefined;

  if (isIndexable) {
    switch (section.type) {
      case 'deals':
        pathname = ROUTE_BUILDER.dealsSerp(indexableKeyword.slug, indexableLocation.slug);
        break;
      case 'brand_deals':
        const { slug } = section.template;
        pathname = ROUTE_BUILDER.brandDealsSerp(slug, indexableLocation.slug);
        break;
      case 'sistaminuten':
        pathname = ROUTE_BUILDER.sistaMinutenSerp(indexableKeyword.slug, indexableLocation.slug);
        break;
    }
  } else {
    switch (section.type) {
      case 'deals':
        pathname = ROUTE_BUILDER.dealsSerpNoIndex();
        break;
      case 'brand_deals':
        const { slug } = section.template;
        pathname = ROUTE_BUILDER.brandDealsSerpNoIndex(slug);
        break;
      case 'sistaminuten':
        pathname = ROUTE_BUILDER.sistaMinutenSerpNoIndex();
        break;
    }
  }

  const q = section.type === 'brand_deals' ? undefined : keyword;
  const search = buildUrlSearchQuery(
    {
      q,
      location: isMyLocation ? DEFAULT_MY_LOCATION_KEYWORD : locationName,
      sort: _sort,
      placeId,
      ...(location?.type === 'myLocation' ? { lat: location.lat, lon: location.lon } : {}),
    },
    isIndexable,
  );

  return { pathname, search };
}

function DealsSectionPreview({
  section,
  dispatch,
  placeId,
  onCardClick,
}: {
  section: DealsSearchPreviewResponse['sections'][number];
  dispatch: AppDispatch;
  placeId?: number;
  onCardClick?: ({
    type,
    item,
  }: { type: 'sistaminuten'; item: SistaminutenSearchItem } | { type: 'deal'; item: DealSearchItem }) => void;
}) {
  const { isMobileView } = useMobileView();
  const hasSectionItems = section.items && section.items.length > 0;
  const to = useMemo(() => buildSectionLink(section, placeId), [section, placeId]);

  if (!hasSectionItems) {
    return null;
  }

  const isBrandDealSponsoredBlock = section.type === 'brand_deals' && section.template;
  const themeColorHex = isBrandDealSponsoredBlock && section.template?.backgroundColor;
  const onDealCardClick = (item: DealSearchItem) => onCardClick?.({ type: 'deal', item });
  const onMerchantCardClick = (item: SistaminutenSearchItem) => onCardClick?.({ type: 'sistaminuten', item });

  const toWithSearchContext = to && { ...to, state: { searchContext: SEARCH_CONTEXT.DEALS_PREVIEW_SECTION } };
  const viewAllProps = getViewAllCardProps(section, toWithSearchContext);
  const categoryHeaderLink = toWithSearchContext
    ? { to: toWithSearchContext, onClick: clearSmtOnlySearchToggle }
    : undefined;

  const title = (() => {
    if (isMobileView) {
      return (
        <div className="flex flex-col">
          <span className="text-h-m text-fg_primary">{section.title}</span>
          {section.subtitle && <span className="text-fg_secondary text-m font-normal">{section.subtitle}</span>}
        </div>
      );
    }
    return (
      <Fragment>
        {section.title}
        {section.subtitle && (
          <Fragment>
            , <span className="text-fg_secondary font-normal">{section.subtitle}</span>
          </Fragment>
        )}
      </Fragment>
    );
  })();

  const renderContent = (section: DealsSearchPreviewResponse['sections'][number]) => {
    const cardWrapperClassName = classnames('', section.layout === 'slider' ? HORIZONTAL_CARD_CONTAINER_CLASSNAME : '');
    return section.items.map((item) => (
      <div key={item.id} className={cardWrapperClassName}>
        {(() => {
          switch (section.type) {
            case 'deals':
            case 'brand_deals': {
              return renderDealCard({
                item,
                searchParams: filterEmptyValues({
                  q: section.searchParams.keyword,
                  sort: section.searchParams.sort,
                }),
                dispatch,
                onClick: onDealCardClick,
              });
            }
            case 'sistaminuten': {
              return renderSistaminutenCard({
                item,
                searchParams: filterEmptyValues({
                  q: section.searchParams.keyword,
                  sort: section.searchParams.sort,
                }),
                dispatch,
                onClick: onMerchantCardClick,
              });
            }
          }
        })()}
      </div>
    ));
  };

  switch (section.layout) {
    case 'slider': {
      const horizontalScrollSection = (
        <HorizontalScrollSection
          categoryHeader={{ title, titleAs: 'h3', link: categoryHeaderLink }}
          description={
            isBrandDealSponsoredBlock
              ? _t(`${baseTranslationKey}.brand-sponsor-block.description`, { brandName: section.template.name })
              : undefined
          }
          className="pb-xl"
          showPagination
          showFadeOverlay={!isBrandDealSponsoredBlock}
          scrollButtonVariant={isBrandDealSponsoredBlock ? 'link' : 'secondary'}
          logoImgUrl={isBrandDealSponsoredBlock && !isMobileView ? section.template?.logoImageUrlSecondary : undefined}
          trailingExtra={
            Boolean(section.hasMore) && (
              <div key={`view-all-${section.type}`} className={HORIZONTAL_CARD_CONTAINER_CLASSNAME}>
                <ViewAllCard {...viewAllProps} onClick={clearSmtOnlySearchToggle} sectionType={section.type} />
              </div>
            )
          }
          scrollSnap
          scrollContainerClassName="py-md md:py-xl">
          {renderContent(section)}
        </HorizontalScrollSection>
      );

      return isBrandDealSponsoredBlock ? (
        <div
          className="pt-2xl pb-xl 2xl:pl-2xl 2xl:rounded-xl"
          style={{ ...(themeColorHex && { backgroundColor: themeColorHex }) }}>
          {horizontalScrollSection}
        </div>
      ) : (
        <Fragment>{horizontalScrollSection}</Fragment>
      );
    }
    case 'grid': {
      const gridViewAllTo = viewAllProps?.to
        ? {
            ...viewAllProps.to,
            state: { ...viewAllProps.to.state, scrollToItemIndex: Math.max(0, section.items.length - 1) },
          }
        : undefined;

      return (
        <div>
          <GridLayoutSection
            categoryHeader={{ title, titleAs: 'h3', link: categoryHeaderLink }}
            className="px-md"
            gridContainerClassName="py-md md:py-lg">
            {renderContent(section)}
          </GridLayoutSection>
          {viewAllProps && section.hasMore && (
            <div className="py-xl mx-auto w-full" style={{ maxWidth: 262 }}>
              <LinkButton
                variant="primary"
                label={_t('showAll')}
                block
                to={gridViewAllTo}
                onClick={clearSmtOnlySearchToggle}
              />
            </div>
          )}
        </div>
      );
    }
  }
}

type ViewAllCardProps = {
  sectionType?: DealsSearchPreviewResponse['sections'][number]['type'];
  title?: string;
  description?: string;
  to?: SectionLink;
  onClick?: () => void;
};

type SortSuffix =
  | ''
  | 'SortByPopular'
  | 'SortByClosest'
  | 'SortByHighestDiscount'
  | 'SortByReviewsScore'
  | 'SortByAvailability';
type PhraseKey = `deals${SortSuffix}` | `brandDeals${SortSuffix}` | 'smt';

function getPhraseKey(section: DealsSearchPreviewResponse['sections'][number]): PhraseKey {
  if (section.type === 'sistaminuten') return 'smt';
  const prefix = section.type === 'brand_deals' ? 'brandDeals' : 'deals';
  switch (section.searchParams.sort) {
    case 'popular':
      return `${prefix}SortByPopular`;
    case 'closest':
      return `${prefix}SortByClosest`;
    case 'highest_discount':
    case 'smt_discount':
      return `${prefix}SortByHighestDiscount`;
    case 'reviews_score':
      return `${prefix}SortByReviewsScore`;
    case 'availability':
      return `${prefix}SortByAvailability`;
    default:
      return prefix;
  }
}

function getViewAllCardProps(
  section: DealsSearchPreviewResponse['sections'][number],
  to: SectionLink,
): ViewAllCardProps | null {
  if (!to) return null;

  const location = section.searchParams.location?.name;
  const preposition = location ? getLocationPreposition(location) : undefined;
  const brandName = section.type === 'brand_deals' ? section.template.name : undefined;

  const title = _t(`${baseTranslationKey}.viewAll.title`);
  const phrase = _t(`${baseTranslationKey}.viewAll.phrase.${getPhraseKey(section)}`, { brandName });

  const description = location
    ? _t(`${baseTranslationKey}.viewAll.showAllWithLocation`, { phrase, preposition, location })
    : _t(`${baseTranslationKey}.viewAll.showAll`, { phrase });

  return { to, title, description };
}

export const ViewAllCard = ({ title, description, to, onClick }: ViewAllCardProps) => {
  return (
    <div
      className="p-3xl bg-bg_neutral gap-3xl drop-shadow-xs relative flex flex-col justify-center rounded-md"
      style={{ height: CARD_HEIGHT, maxWidth: 402 }}>
      <div className="space-y-xs flex flex-col">
        <img src="/images/deal-basket.png" width={96} height={85} />
        <span className="text-fg_link text-h-l mb-xs font-semibold">{title}</span>
        <span className="text-fg_secondary text-l">{description}</span>
      </div>
      <LinkButton
        block
        variant="primary"
        size="lg"
        className="after:absolute after:inset-0 after:content-['']"
        to={to}
        onClick={onClick}>
        {_t('showAll')}
      </LinkButton>
    </div>
  );
};

export const DealsSectionPreviewSkeleton = ({ cards }: { cards: number }) => (
  <HorizontalScrollSection className="pb-xl" showPagination scrollSnap scrollContainerClassName="py-md md:py-xl">
    {Array.from({ length: cards }).map((_, i) => (
      <div key={i} className={HORIZONTAL_CARD_CONTAINER_CLASSNAME}>
        <MPServiceCardSkeleton />
      </div>
    ))}
  </HorizontalScrollSection>
);

export default DealsSectionPreview;
